<template>
  <div class="w-full">
    <v-btn
      class="upload-btn"
      text
      depressed
      @click.prevent="_triggerUploadFile()"
      :disabled="readonly || $store.getters.isLoading"
      :loading="$store.getters.isUploading"
      :color="error ? 'error' : 'primary'"
      >{{ $t(uploadText) }}</v-btn
    >
    <small v-if="error" class="d-block caption error--text mt-1">{{ $t('message.formRequired') }}</small>

    <v-file-input
      v-show="false"
      :id="fileInputKey"
      :accept="fileFormatStr"
      @change="_getUploadFile($event)"
      :disabled="$store.getters.isLoading || $store.getters.isUploading"
      v-model="fileData"
    ></v-file-input>

    <template v-if="files.length > 0">
      <ul class="file-preview-list" v-if="enablePreview">
        <li v-for="(item, i) in files" :key="i">
          <v-img class="preview-img" :src="_getFile(item.file)" @click="_previewFile(item.file)"></v-img>
          <v-btn v-if="!readonly" text depressed x-small color="error" class="mt-1" @click="_removeFile(i)">{{
            $t('delete')
          }}</v-btn>
        </li>
      </ul>
      <ul class="file-list" v-else>
        <li v-for="(item, i) in files" :key="i">
          <a @click.stop="_previewFile(item.file)">{{ item.name }}</a>
          <v-btn v-if="!readonly" text depressed x-small v-ripple="false" icon class="ml-4" @click="_removeFile(i)">
            <v-icon size="14" color="error">{{ icon.mdiClose }}</v-icon>
          </v-btn>
        </li>
      </ul>
    </template>

    <v-text-field v-show="false" :value="fileValue" :rules="[v => !required || $validate.required(v)]" />
  </div>
</template>

<script>
import { mdiClose } from '@mdi/js'

export default {
  name: 'FormFileUploader',
  props: {
    fileInputKey: {
      type: String,
      default: 'upload_file',
      required: true,
    },
    files: {
      type: Array, // {name: '', file: ''}
      default: () => [],
      required: true,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    acceptFormat: {
      type: String,
      default: 'imageWithPDF',
      required: true,
    },
    uploadText: {
      type: String,
      default: 'upload',
      require: false,
    },
    dispatchUpdateOnChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    single: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    enablePreview: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    uploading() {
      return this.$store.getters.isUploading
    },
    fileFormatStr() {
      return this.$common.getAcceptFileFormat(this.acceptFormat).formatStr
    },
    fileFormatArr() {
      return this.$common.getAcceptFileFormat(this.acceptFormat).formatArr
    },
  },
  watch: {
    files: {
      handler(val) {
        this.fileValue = null
        if (this.$validate.DataValid(val) && val.length > 0) {
          this.fileValue = val[0].file
        }
      },
      immediate: true,
      deep: true,
    },
  },
  data: () => ({
    icon: {
      mdiClose,
    },
    fileValue: null,

    fileData: null,

    error: false,
  }),
  methods: {
    check() {
      this.error = false
      if (this.required && (!this.$validate.DataValid(this.files) || this.files.length < 1)) {
        this.error = true
        return false
      }

      return true
    },

    _triggerUploadFile() {
      if (this.$validate.DataValid(this.fileInputKey) && document.getElementById(this.fileInputKey)) {
        document.getElementById(this.fileInputKey).click()
      }
    },

    _getFile(file) {
      return `${this.$mediaPath}${file}`
    },

    async _getUploadFile(file) {
      if (file) {
        const check = this.$validate.validateFileInput(file, this.fileFormatArr)
        if (this.$validate.DataValid(check)) {
          this.$store.dispatch('toggleSnack', {
            message: check,
            type: 'error',
          })
        } else {
          this.$store.dispatch('setUploadLoading', true)

          const reader = new FileReader()
          reader.onload = async event => {
            const fileType = file.type.split('/')[1]
            const payload = {
              upload_data: event.target.result,
              upload_file_type: fileType,
            }

            try {
              const data = await this.$Fetcher.UploadFile(payload)

              let fileList = this.$common.duplicateData(this.files)
              if (this.single) {
                fileList = [{ name: file.name, file: data }]
              } else {
                fileList.push({ name: file.name, file: data })
              }

              this.$emit('update:files', fileList)

              if (this.dispatchUpdateOnChange === true) {
                this.$store.dispatch('setDataIsUpdated', true)
              }

              this.$store.dispatch('toggleSnack', {
                message: 'message.uploadSuccess',
                type: 'success',
              })
            } catch (err) {
              this.$common.error(err)
              this.$store.dispatch('toggleSnack', {
                message: 'message.uploadFail',
                type: 'error',
              })
            } finally {
              this.$store.dispatch('setUploadLoading', false)
            }
          }
          reader.readAsDataURL(file)
        }
      }
    },

    _removeFile(index) {
      if (this.$store.getters.isLoading || this.$store.getters.isUploading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }
      const fileList = this.$common.duplicateData(this.files)
      fileList.splice(index, 1)
      this.$emit('update:files', fileList)
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
    },

    _previewFile(file) {
      if (!file) return
      if (this.acceptFormat === 'image' || this.acceptFormat === 'video') {
        this.$store.dispatch('openMediaPopup', {
          media: `${this.$mediaPath}${file}`,
          isVideo: this.acceptFormat === 'video',
        })
      } else {
        const type = file.split('.').pop()
        if (['png', 'jpeg', 'jpg'].includes(type.toLowerCase())) {
          this.$store.dispatch('openMediaPopup', {
            media: `${this.$mediaPath}${file}`,
          })
        } else {
          window.open(`${this.$mediaPath}${file}`, '_blank')
        }
      }
    },
  },
}
</script>
